import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Avatar,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { buttonStyles, textFieldStyles } from '../../../Login/LoginPageStyles';
import { PhotoCamera } from '@mui/icons-material';
import { db, storage } from '../../../firebase';
import { doc, setDoc } from 'firebase/firestore';  
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import AvatarEditorDialog from '../AvatarEditorDialog';

const AddUser = ({ open, onClose }) => {
  const [id, setId] = useState('');
  const [contract, setContract] = useState('');
  const [cnic, setCnic] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [number, setNumber] = useState('');
  const [position, setPosition] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [joiningDate, setJoiningDate] = useState('');
  const [weeklyHours, setWeeklyHours] = useState('');
  const [hourlyWage, setHourlyWage] = useState('');
  const [allowedLeave, setAllowedLeave] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [role, setRole] = useState('admin');
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false);
  const [croppedAvatar, setCroppedAvatar] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' or 'error'
  const [loading, setLoading] = useState(false); // New state for loading

  const handleIdChange = (event) => setId(event.target.value);
  const handleContractChange = (event) => setContract(event.target.value);
  const handleNameChange = (event) => setName(event.target.value);
  const handleAddressChange = (event) => setAddress(event.target.value);
  const handleNumberChange = (event) => setNumber(event.target.value);
  const handlePositionChange = (event) => setPosition(event.target.value);
  const handleEmailChange = (event) => setEmail(event.target.value);
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const handleJoiningDateChange = (event) => setJoiningDate(event.target.value);
  const handleWeeklyHoursChange = (event) => setWeeklyHours(event.target.value); // Handle weekly hours change
  const handleHourlyWageChange = (event) =>setHourlyWage(event.target.value);
  const handleAllowedLeaveChange = (event) =>setAllowedLeave(event.target.value);
  const handleAvatarChange = (event) => {
    setAvatar(event.target.files[0]);
    setAvatarEditorOpen(true);  // Open the editor when a file is selected
  };

  const handleCnicChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove all non-digit characters
  
    if (value.length <= 5) {
      // If the value is 5 or fewer digits, no dash is needed yet
     // value = value;
    } else if (value.length <= 12) {
      // If the value is between 6 and 12 digits, add the first dash
      value = value.slice(0, 5) + '-' + value.slice(5);
    } else {
      // If the value is more than 12 digits, add both dashes
      value = value.slice(0, 5) + '-' + value.slice(5, 12) + '-' + value.slice(12, 13);
    }
  
    setCnic(value); // Update the state with the formatted value
  };

  const handleSaveCroppedAvatar = (croppedImage) => {
    setCroppedAvatar(croppedImage);
    setAvatarEditorOpen(false);
  };

  const handleSubmit = async () => {
    setLoading(true); // Set loading to true when starting submission
    try {
      let avatarUrl = null;

      if (croppedAvatar) {
        // Convert base64 to Blob
        const blob = await (await fetch(croppedAvatar)).blob();
        const avatarRef = ref(storage, `employeeAvatar/${name}`);
        await uploadBytes(avatarRef, blob);
        avatarUrl = await getDownloadURL(avatarRef); // Get the URL of the uploaded image
      }

      await setDoc(doc(db, "employees", id), {
        id,
        contract,
        cnic,
        name,
        address,
        number,
        position,
        email,
        password,
        joiningDate,
        weeklyHours,  
        hourlyWage,
        allowedLeave,
        avatar: avatarUrl,  
        role                
      });

      setSnackbarMessage('Employee successfully added!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      // Reset form fields
      setId('');
      setContract('');
      setCnic('');
      setName('');
      setAddress('');
      setPosition('');
      setEmail('');
      setNumber('');
      setPassword('');
      setJoiningDate('');
      setWeeklyHours('');
      setHourlyWage('');
      setAllowedLeave('');
      setAvatar(null);
      setCroppedAvatar(null);
      setRole('admin');  // Reset role selection to default

    } catch (error) {
      setSnackbarMessage('Error adding employee. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      console.error("Error adding employee: ", error);
    } finally {
      setLoading(false); // Set loading to false when done
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Add New Employee</DialogTitle>
        <DialogContent>
          <TextField
            label="Id"
            variant="outlined"
            fullWidth
            value={id}
            onChange={handleIdChange}
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Reference/Contract"
            variant="outlined"
            fullWidth
            value={contract}
            onChange={handleContractChange}
            sx={{...textFieldStyles, mb: 2}}
          />
          <TextField
            label="Cnic Number"
            variant="outlined"
            fullWidth
            value={cnic}
            onChange={handleCnicChange}
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Name"
            variant="outlined"
            fullWidth
            value={name}
            onChange={handleNameChange}
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Address"
            variant="outlined"
            fullWidth
            value={address}
            onChange={handleAddressChange}
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Phone Number"
            variant="outlined"
            fullWidth
            value={number}
            onChange={handleNumberChange}
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Position"
            variant="outlined"
            fullWidth
            value={position}
            onChange={handlePositionChange}
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            value={email}
            onChange={handleEmailChange}
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Password"
            variant="outlined"
            fullWidth
            value={password}
            onChange={handlePasswordChange}
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Joining Date"
            type="date"
            variant="outlined"
            fullWidth
            value={joiningDate}
            onChange={handleJoiningDateChange}
            sx={{...textFieldStyles, mb: 2 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Weekly Hours"
            variant="outlined"
            fullWidth
            value={weeklyHours}
            onChange={handleWeeklyHoursChange} 
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Hourly Wage"
            variant="outlined"
            fullWidth
            value={hourlyWage}
            onChange={handleHourlyWageChange} 
            sx={{...textFieldStyles, mb: 2 }}
          />
          <TextField
            label="Allowed Paid Leave"
            variant="outlined"
            fullWidth
            value={allowedLeave}
            onChange={handleAllowedLeaveChange} 
            sx={{...textFieldStyles, mb: 2 }}
          />
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">Role</FormLabel>
            <RadioGroup
              aria-label="role"
              name="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <FormControlLabel value="admin" control={<Radio />} label="Admin" />
              <FormControlLabel value="employee" control={<Radio />} label="Employee" />
            </RadioGroup>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Button variant="contained" component="label" sx={{ backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2c8f39' } }}>
              Upload Picture
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleAvatarChange}
              />
              <IconButton component="span">
                <PhotoCamera />
              </IconButton>
            </Button>
            {croppedAvatar && (
              <Avatar
                src={croppedAvatar}
                sx={{ 
                  mt: 2, 
                  width: 56, 
                  height: 56, 
                  objectFit: 'cover', 
                  alignItems: 'center', 
                }}
              />
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: 'red' }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" 
           sx={{...buttonStyles, mt:0, position: 'relative' }} 
           disabled={loading}>
            {loading && (
              <CircularProgress size={24} sx={{ position: 'absolute', left: '50%', top: '50%', marginLeft: '-12px', marginTop: '-12px', color: 'white' }} />
            )}
            <span style={{ visibility: loading ? 'hidden' : 'visible' }}>Add Employee</span>
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={8000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          <Button color="inherit" onClick={handleSnackbarClose}>
            Close
          </Button>
        }
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <AvatarEditorDialog
        open={avatarEditorOpen}
        onClose={() => setAvatarEditorOpen(false)}
        onSave={handleSaveCroppedAvatar}
        image={avatar}
      />
    </>
  );
};

export default AddUser;
